import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { GridFilterItem } from '@mui/x-data-grid-premium';
import { NameIdObj, NameIdObjString } from 'common/models/GenericTypes';
import JobsGridComponent from 'components/Grids/JobsGrid';
import JobsKanbanComponent from 'components/Kanbans/JobsKanban';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import MultipleStringValuesPicker from 'components/Pickers/MultipleStringValuesPicker';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GetSettingBySettingName, UpdateSettingBySettingName } from 'services/UsersService';
import { JobTypeOptions } from 'util/Definitions/Jobs';

interface Props {
  loadingHandler?: (isLoading: boolean) => void;
}

interface FilterSettings {
  source: string;
  sourceIds: number[];
  jobTypes: string[];
}

const defaultJobViewSettingName = 'DefaultJobsViewDashboard';
const filterSettingsName = 'OpenJobsTabFilterSettings';
const filterItemId = 'dashboard-jobs-filter';

export default function HomeOpenJobsTabContent({ loadingHandler }: Props) {
  const [isSetupFinished, setIsSetupFinished] = useState(false);
  const [jobTypesFilter, setJobTypesFilter] = useState(['All']);
  const [jobsView, setJobsView] = useState<'list' | 'kanban'>('kanban');
  const [sourceType, setSourceType] = useState('currentuser');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);

  useEffect(() => {
    const getSettings = async () => {
      const [view, filterSettings] = await Promise.all([
        GetSettingBySettingName(defaultJobViewSettingName),
        GetSettingBySettingName(filterSettingsName),
      ]);

      if (view) setJobsView(view === 'list' ? 'list' : 'kanban');
      if (filterSettings) {
        const parsed = JSON.parse(filterSettings) as FilterSettings;
        if (parsed.source) setSourceType(parsed.source);
        if (parsed.jobTypes) setJobTypesFilter(parsed.jobTypes);
        if (parsed.source === 'groups' && parsed.sourceIds) setSelectedGroups(parsed.sourceIds);
        if (parsed.source === 'consultants' && parsed.sourceIds) setSelectedUsers(parsed.sourceIds);
      }

      setIsSetupFinished(true);
    };
    getSettings();
  }, []);

  const jobsViewChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const value = isChecked ? 'list' : 'kanban';
    setJobsView(value);
    UpdateSettingBySettingName(defaultJobViewSettingName, value);
  }, []);

  const openJobsViewSwitch = useMemo(
    () => (
      <span>
        <span>Kanban</span>
        <Switch checked={jobsView === 'list'} onChange={jobsViewChangeHandler} />
        <span>List</span>
      </span>
    ),
    [jobsView, jobsViewChangeHandler]
  );

  const dataSource = useMemo(() => {
    if (sourceType === 'company') return 'company-jobs';
    if (sourceType === 'consultants') return 'open-jobs-by-user-ids';
    if (sourceType === 'groups') return 'open-jobs-by-group-ids';
    if (sourceType === 'currentuser') return 'dashboard';
    return 'dashboard';
  }, [sourceType]);

  const sourceIdArray = useMemo(() => {
    if (sourceType === 'groups') return selectedGroups;
    if (sourceType === 'consultants') return selectedUsers;
    return undefined;
  }, [sourceType, selectedGroups, selectedUsers]);

  const gridFilterItem = useMemo<GridFilterItem | undefined>(() => {
    if (jobTypesFilter.includes('All')) return undefined;
    return {
      id: filterItemId,
      field: 'typeName',
      operator: 'isAnyOf',
      value: jobTypesFilter,
    };
  }, [jobTypesFilter]);

  const saveFilterSettingsCallback = useCallback(
    async (source: string, sourceIds: number[] | undefined, jobTypes: string[]) => {
      const filterSettings: FilterSettings = {
        source: source,
        sourceIds: sourceIds ?? [],
        jobTypes: jobTypes,
      };
      UpdateSettingBySettingName(filterSettingsName, JSON.stringify(filterSettings));
    },
    []
  );

  const sourceTypeChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSourceType(value);
      saveFilterSettingsCallback(value, sourceIdArray, jobTypesFilter);
    },
    [sourceIdArray, jobTypesFilter, saveFilterSettingsCallback]
  );

  const jobTypeChangeHandler = useCallback(
    (values: NameIdObjString[]) => {
      const ids = values.map((v) => v.id);
      setJobTypesFilter(ids);
      saveFilterSettingsCallback(sourceType, sourceIdArray, ids);
    },
    [sourceType, sourceIdArray, saveFilterSettingsCallback]
  );

  const consultantsChangeHandler = useCallback(
    (users: NameIdObj[]) => {
      const ids = users.map((u) => u.id);
      setSelectedUsers(ids);
      saveFilterSettingsCallback(sourceType, ids, jobTypesFilter);
    },
    [sourceType, jobTypesFilter, saveFilterSettingsCallback]
  );

  const groupsChangeHandler = useCallback(
    (groups: NameIdObj[]) => {
      const ids = groups.map((g) => g.id);
      setSelectedGroups(ids);
      saveFilterSettingsCallback(sourceType, ids, jobTypesFilter);
    },
    [sourceType, jobTypesFilter, saveFilterSettingsCallback]
  );

  return (
    <>
      <Box display="flex" gap={1} mb={1}>
        <TextField
          select
          label="Source Type"
          value={sourceType}
          onChange={sourceTypeChangeHandler}
          sx={{ flex: '1 1 0' }}
        >
          <MenuItem value="company">All</MenuItem>
          <MenuItem value="consultants">Consultant</MenuItem>
          <MenuItem value="currentuser">Current User</MenuItem>
          <MenuItem value="groups">Group</MenuItem>
        </TextField>
        <MultipleStringValuesPicker
          label="Job Type"
          options={JobTypeOptions}
          value={jobTypesFilter}
          onSelect={jobTypeChangeHandler}
          sx={{ flex: '1 1 0' }}
        />
      </Box>
      {sourceType === 'consultants' && (
        <MultipleUserPicker
          value={selectedUsers}
          onSelect={consultantsChangeHandler}
          label="Consultants"
          sx={{ mb: 1 }}
        />
      )}
      {sourceType === 'groups' && (
        <MultipleGroupPicker
          value={selectedGroups}
          onSelect={groupsChangeHandler}
          label="Groups"
          sx={{ mb: 1 }}
        />
      )}
      {isSetupFinished && jobsView === 'kanban' && (
        <JobsKanbanComponent
          source={dataSource}
          sourceIdArray={sourceIdArray}
          extraAction={openJobsViewSwitch}
          loadingHandler={loadingHandler}
          jobTypeFilterValues={jobTypesFilter}
          hideJobTypeFilter
        />
      )}
      {isSetupFinished && jobsView === 'list' && (
        <JobsGridComponent
          source={dataSource}
          sourceIdArray={sourceIdArray}
          jobState={1}
          loadingHandler={loadingHandler}
          extraActions={openJobsViewSwitch}
          gridName="dashboard/Jobs"
          filterItem={gridFilterItem}
          filterItemId={filterItemId}
        />
      )}
    </>
  );
}
